/**
 * dubSyncUtils.js — shared pure utilities for DubSync cue manipulation.
 */

/**
 * Time-overlap AutoSplit.
 *
 * Produces exactly as many segments as there are original subtitle cues by
 * slicing the dubbed cue text based on time overlap.  For each original cue
 * we find every dubbed cue whose time window overlaps, compute how much of
 * that dubbed cue's duration falls inside the original window, and extract
 * the proportional word slice.  The output cue keeps the original cue's
 * start/end time so the video timeline remains correct.
 *
 * If dubbed and original cues are already 1:1 (or dubbed has MORE cues),
 * the function returns dubSyncCues unchanged — there is nothing to split.
 *
 * Ideal workflow: Transcribe → AutoSplit → Align
 *  - Whisper may return only a handful of large cues for the dubbed audio.
 *  - AutoSplit segments them to match original subtitle count/structure.
 *  - Align then has a clean 1-cue-per-original mapping to work with.
 *
 * @param {Array<{id,startTime,endTime,text}>} dubSyncCues   — dubbed cues (may be fewer than original)
 * @param {Array<{id,startTime,endTime,text}>} originalCues  — original subtitle cues (defines structure)
 * @returns {Array<{id,startTime,endTime,text}>}             — one segment per originalCue
 */
export function autoSplitCues(dubSyncCues, originalCues) {
    if (!originalCues?.length || !dubSyncCues?.length) return dubSyncCues;

    // Already 1:1 or more — no split needed; return as-is to avoid scrambling
    if (dubSyncCues.length >= originalCues.length) return dubSyncCues;

    const ts = Date.now();
    const result = [];

    for (let i = 0; i < originalCues.length; i++) {
        const oc = originalCues[i];
        const ocStart = oc.startTime;
        const ocEnd   = oc.endTime;

        // Find dubbed cues whose time window overlaps [ocStart, ocEnd]
        const overlapping = dubSyncCues.filter(
            (dc) => dc.endTime > ocStart && dc.startTime < ocEnd,
        );

        if (overlapping.length === 0) {
            // Gap in the original — produce an empty placeholder cue
            result.push({
                id:        `dubsplit_${i}_${ts}`,
                startTime: ocStart,
                endTime:   Math.max(ocStart + 0.1, ocEnd),
                text:      '',
            });
            continue;
        }

        // For each overlapping dubbed cue, extract the word slice that falls
        // inside [ocStart, ocEnd] based on time position within that dubbed cue.
        const words = [];
        for (const dc of overlapping) {
            const dcDuration   = Math.max(0.001, dc.endTime - dc.startTime);
            const overlapStart = Math.max(ocStart, dc.startTime);
            const overlapEnd   = Math.min(ocEnd,   dc.endTime);
            const overlapLen   = Math.max(0, overlapEnd - overlapStart);

            const dcWords = (dc.text || '').split(/\s+/).filter(Boolean);
            if (dcWords.length === 0) continue;

            // How far into the dubbed cue does the overlap start?
            const startFrac  = (overlapStart - dc.startTime) / dcDuration;
            const wordStart  = Math.round(startFrac * dcWords.length);
            // How many words does the overlap proportion cover?
            const overlapFrac = overlapLen / dcDuration;
            const wordCount   = Math.max(1, Math.round(dcWords.length * overlapFrac));
            const wordEnd     = Math.min(dcWords.length, wordStart + wordCount);

            words.push(...dcWords.slice(wordStart, wordEnd));
        }

        result.push({
            id:        `dubsplit_${i}_${ts}`,
            startTime: ocStart,
            endTime:   Math.max(ocStart + 0.1, ocEnd),
            text:      words.join(' '),
        });
    }

    return result;
}
